import { refreshTokenApi } from "@/api/services/admin/auth";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { ERROR_STATUS_CODES } from "@/constants/error";
import { EVENTS } from "@/constants/socket";
import { logout } from "@/utils/api";
import { toast } from "@/utils/toast";
import Cookies from "js-cookie";
import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { io, Socket } from "socket.io-client";

const SOCKET_SERVER_URL = process.env.REACT_APP_API_HOST || "http://localhost:3000";

interface ISocketContext {
  socket: Socket | null;
}

interface SocketProviderProps {
  children: ReactNode;
}

const SocketContext = createContext<ISocketContext | undefined>(undefined);

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);

  const connectSocket = () => {
    const token = Cookies.get("admin-token");
    if (!token) return;

    const socketInstance = io(SOCKET_SERVER_URL, {
      extraHeaders: { Authorization: `Bearer ${token}` },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });

    const pingInterval = setInterval(() => {
      if (socketInstance.connected) {
        socketInstance.emit(EVENTS.GLOBAL.PING);
      }
    }, 60000); // send ping every 60s

    socketInstance.on("connect", () => {
      setConnected(true);
      console.log("Socket connected");
    });

    socketInstance.on("disconnect", (reason) => {
      setConnected(false);
      console.log("Socket disconnected:", reason);
      if (reason === "io server disconnect") {
        socketInstance.connect();
      }
    });

    setSocket(socketInstance);

    return () => {
      clearInterval(pingInterval);
      socketInstance.disconnect();
    };
  };

  async function handleUnauthorized() {
    try {
      const email = Cookies.get("email");
      const refreshToken = Cookies.get("admin-refresh-token");
      if (!email || !refreshToken) {
        logout();
        return;
      }
      await refreshTokenApi({ email, refreshToken });
      connectSocket();
    } catch (error) {
      logout();
    }
  }

  useEffect(() => {
    const cleanup = connectSocket();
    return cleanup;
  }, []);

  useEffect(() => {
    if (!socket) return;

    socket.on(EVENTS.GLOBAL.ERROR, (message) => {
      switch (message) {
        case ERROR_STATUS_CODES.UNAUTHORIZED:
          handleUnauthorized();
          break;
        default:
          toast.error(message);
      }
    });

    socket.on(EVENTS.GLOBAL.CONNECTED, () => {
      setConnected(true);
    });

    socket.on(EVENTS.GLOBAL.PONG, () => {
      console.log("Received pong from server");
    });

    const handleVisibilityChange = () => {
      if (!document.hidden && !socket?.connected) {
        console.log("Tab active, reconnecting socket...");
        connectSocket();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      socket.off(EVENTS.GLOBAL.ERROR);
      socket.off(EVENTS.GLOBAL.CONNECTED);
      socket.off(EVENTS.GLOBAL.PONG);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [socket]);

  if (!connected) return <LoadingPage />;

  return <SocketContext.Provider value={{ socket }}>{children}</SocketContext.Provider>;
};
